/**
 * Color scales for heat map choropleth rendering.
 * Each scale is an array of CSS colors from low to high values.
 * Used by both MapLibre fill-color expressions and the legend gradient.
 */
export const HEATMAP_COLOR_SCALES = {
  YlOrRd: ['#ffffb2', '#fecc5c', '#fd8d3c', '#f03b20', '#bd0026'],
  BuPu: ['#edf8fb', '#b3cde3', '#8c96c6', '#8856a7', '#810f7c'],
  RdYlGn: ['#d73027', '#fc8d59', '#fee08b', '#91cf60', '#1a9850'],
  GnBu: ['#f0f9e8', '#bae4bc', '#7bccc4', '#43a2ca', '#0868ac'],
  Greens: ['#edf8e9', '#bae4b3', '#74c476', '#31a354', '#006d2c'],
  OrRd: ['#fef0d9', '#fdcc8a', '#fc8d59', '#e34a33', '#b30000']
}

/**
 * Seismic hazard color palette (8 colors, indices 0-6 used for risk levels).
 */
const seismicPalette = ['#2b83ba', '#64b5a5', '#abdda4', '#ffffbf', '#fdae61', '#f46d43', '#d7191c', '#a50026']

/**
 * Build a MapLibre interpolate expression for fill-color
 * based on a property field and min/max data range.
 */
export function buildColorExpression(field, min, max, colorScale) {
  const colors = HEATMAP_COLOR_SCALES[colorScale] || HEATMAP_COLOR_SCALES.YlOrRd
  const steps = colors.length
  const range = max - min || 1

  const expr = ['interpolate', ['linear'], ['coalesce', ['get', field], 0]]
  for (let i = 0; i < steps; i++) {
    const value = min + (range * i) / (steps - 1)
    expr.push(value, colors[i])
  }
  return expr
}

/**
 * Build a MapLibre step expression for discrete risk-level coloring.
 */
export function buildStepColorExpression(field, colors) {
  // step expression: ['step', input, defaultColor, stop1, color1, stop2, color2, ...]
  const expr = ['step', ['coalesce', ['get', field], 0], colors[0]]
  for (let i = 1; i < colors.length; i++) {
    expr.push(i, colors[i])
  }
  return expr
}

/**
 * Build a MapLibre step expression keyed on a numeric property with explicit
 * break/color pairs. `breaks` and `colors` are parallel arrays where colors[0]
 * is the default (values below breaks[0]) and colors[i+1] applies at breaks[i].
 *   buildThresholdStepExpression('pctServed', [50,65,80,90,95,100], [c0,c1,...,c6])
 */
export function buildThresholdStepExpression(field, breaks, colors) {
  const expr = ['step', ['get', field], colors[0]]
  for (let i = 0; i < breaks.length; i++) {
    expr.push(breaks[i], colors[i + 1])
  }
  return expr
}

/**
 * ESRI Demographics Feature Service base URLs.
 */
export const ESRI_DEMOGRAPHICS_BASE_URL =
  'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/Esri_Updated_Demographics_Variables/FeatureServer'

export const ESRI_DEMOGRAPHICS_NON_NESTED_URL =
  'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/Esri_Updated_Demographics_Popular_Variables_Subscriber_2022/FeatureServer'

/**
 * Zoom-level to ESRI geography layer mapping.
 * Each entry defines a zoom range and the corresponding layer ID.
 * Entries with server: 'nonNested' use the non-nested feature service.
 */
export const esriDemographicZoomLayers = [
  { name: 'State', layer: 1, minZoom: 0, maxZoom: 6 },
  { name: 'CBSA', layer: 2, minZoom: 7, maxZoom: 8, server: 'nonNested' },
  { name: 'County', layer: 3, minZoom: 9, maxZoom: 9 },
  { name: 'Zip Code', layer: 1, minZoom: 10, maxZoom: 12, server: 'nonNested' },
  { name: 'Tract', layer: 4, minZoom: 13, maxZoom: 20 }
]

/**
 * Heat map layers that do not change with zoom (static overlays).
 */
// Broadband classification breaks/colors (% served). colors[0] applies below the
// first break (< 50%); each subsequent color applies at and above its break.
// NB: hex values were given truncated to 5 digits in the ticket — these are the
// inferred 6-digit values (trailing 0 restored). Confirm if the ramp looks off.
export const BROADBAND_BREAKS = [50, 65, 80, 90, 95, 100]
export const BROADBAND_COLORS = [
  '#ff0000', // < 50
  '#f15000', // 50–65
  '#e39700', // 65–80
  '#d5d500', // 80–90
  '#85c700', // 90–95
  '#3eb900', // 95–100
  '#00ab00' // 100 (fully served)
]

export const heatMapLayers = {
  seismichazards: {
    name: 'seismichazards',
    label: 'USGS Seismic Hazard Rating',
    source: '2018 US Geological Survey',
    legend: ['0', '1', '2', '3', '4', '5', '6'].map((val, i) => ({ label: val, color: seismicPalette[i] })),
    fields: ['OBJECTID', 'Risk_Level'],
    url: 'https://services1.arcgis.com/utFj6xjAHT9GdIit/ArcGIS/rest/services/Intersect_of_Seismic_Hazard_and_USA_Boundaries/FeatureServer/0',
    defaultOff: false
  },
  // FCC Broadband Data Collection (June 2024 vintage) — county-level computed
  // choropleth. Value field `pctServed` is derived client-side after fetch.
  broadband: {
    name: 'broadband',
    label: 'Broadband Availability',
    source: 'FCC Broadband Data Collection (June 2024)',
    url: 'https://services.arcgis.com/jIL9msH9OI208GCb/arcgis/rest/services/FCC_Broadband_Data_Collection_June_2022/FeatureServer/1',
    fields: ['OBJECTID', 'CountyName', 'StateName', 'TotalBSLs', 'ServedBSLs',
      'UnderservedBSLs', 'UnservedBSLs', 'ServedBSLsFiber', 'UniqueProviders'],
    computed: true,
    valueField: 'pctServed',
    breaks: BROADBAND_BREAKS,
    colors: BROADBAND_COLORS,
    legend: [
      { label: '< 50%', color: '#ff0000' },
      { label: '50–65%', color: '#f15000' },
      { label: '65–80%', color: '#e39700' },
      { label: '80–90%', color: '#d5d500' },
      { label: '90–95%', color: '#85c700' },
      { label: '95–100%', color: '#3eb900' },
      { label: '100%', color: '#00ab00' }
    ],
    defaultOff: false
  }
}

/**
 * Get the zoom layer config for the current zoom level.
 * Returns { layer, server, name } or null.
 */
export function getLayerForZoom(zoom) {
  const z = Math.floor(zoom)
  for (const entry of esriDemographicZoomLayers) {
    if (z >= entry.minZoom && z <= entry.maxZoom) {
      return entry
    }
  }
  // Fallback to last entry (Tract) for very high zoom
  return esriDemographicZoomLayers[esriDemographicZoomLayers.length - 1]
}

/**
 * Compute a geometry simplification offset based on zoom level.
 * At low zoom, returns a large offset to reduce polygon detail.
 * At high zoom, returns a small offset to preserve detail.
 * Uses degrees (WGS84) since inSR/outSR are 4326.
 */
function getMaxAllowableOffset(zoom) {
  if (zoom <= 3) { return 0.5 }
  if (zoom <= 5) { return 0.1 }
  if (zoom <= 7) { return 0.05 }
  if (zoom <= 9) { return 0.01 }
  if (zoom <= 12) { return 0.005 }
  return 0.001
}

/**
 * Build a query URL for the ESRI demographics feature service.
 * @param {Object} bounds - { west, south, east, north } in WGS84
 * @param {string} field - ESRI field name (e.g. 'POPDENS_CY')
 * @param {Object} layerConfig - Layer config from getLayerForZoom()
 * @param {number} zoom - Current map zoom level
 * @returns {string} Full query URL (to be passed through the ArcGIS proxy)
 */
export function buildDemographicsQueryUrl(bounds, field, layerConfig, zoom) {
  const baseUrl = layerConfig.server === 'nonNested'
    ? ESRI_DEMOGRAPHICS_NON_NESTED_URL
    : ESRI_DEMOGRAPHICS_BASE_URL
  const envelope = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`
  const params = new URLSearchParams({
    where: '1=1',
    outFields: field,
    geometry: envelope,
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    inSR: '4326',
    outSR: '4326',
    f: 'geojson',
    returnGeometry: 'true',
    maxAllowableOffset: String(getMaxAllowableOffset(zoom != null ? zoom : 10)),
    resultRecordCount: '2000'
  })
  return `${baseUrl}/${layerConfig.layer}/query?${params.toString()}`
}

/**
 * Build a query URL for a static heat map layer (e.g. seismic hazards).
 * @param {Object} bounds - { west, south, east, north } in WGS84
 * @param {Object} layerDef - Layer definition from heatMapLayers
 * @param {number} zoom - Current map zoom level
 * @returns {string} Full query URL
 */
export function buildHeatMapLayerQueryUrl(bounds, layerDef, zoom) {
  const envelope = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`
  const params = new URLSearchParams({
    where: '1=1',
    outFields: layerDef.fields.join(','),
    geometry: envelope,
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    inSR: '4326',
    outSR: '4326',
    f: 'geojson',
    returnGeometry: 'true',
    maxAllowableOffset: String(getMaxAllowableOffset(zoom != null ? zoom : 10)),
    resultRecordCount: '2000'
  })
  return `${layerDef.url}/query?${params.toString()}`
}

/**
 * Compute min and max values for a given field from GeoJSON features.
 */
export function computeMinMax(geojson, field) {
  let min = Infinity
  let max = -Infinity
  const features = (geojson && geojson.features) || []
  for (const f of features) {
    const val = f.properties && f.properties[field]
    if (val != null && isFinite(val)) {
      if (val < min) { min = val }
      if (val > max) { max = val }
    }
  }
  if (!isFinite(min)) { min = 0 }
  if (!isFinite(max)) { max = 0 }
  return { min, max }
}
